import { isAbortOrCanceledError } from "@captions-web/utils/abort-signals";
import { z } from "zod";

import { NetworkAnalyticsDataRecord, NetworkingEvents } from "../analytics/defs/NetworkEvents";

import { ApiHost, ApiResponse, createClientTrace, makeApiRequest } from "./api-client";
import { ApiUnavailableError, KnownApiError } from "./known-api-error";

export type QueryMethod = "POST" | "PUT" | "GET" | "DELETE";

type ExtractParamFromPath<Path, NextPart> = Path extends `:${infer Param}`
  ? Record<Param, string> & NextPart
  : NextPart;

export type ExtractParamsFromPath<Path> = Path extends `${infer Segment}/${infer Rest}`
  ? ExtractParamFromPath<Segment, ExtractParamsFromPath<Rest>>
  : // eslint-disable-next-line @typescript-eslint/ban-types
    ExtractParamFromPath<Path, {}>;

export type NetworkAnalyticsOptions<
  ParamsSchema extends z.ZodTypeAny,
  ResponseSchema extends z.ZodTypeAny,
> =
  // no events will be logged
  | { level: "none" }
  // events will be logged but no request params or response data
  | { level: "basic" }
  // events will be logged with request params and response data that is selected
  | {
      level: "custom";

      // defaults to true
      shouldLogRequest?: (
        status: "success" | "fail" | "cancel",
        params: z.infer<ParamsSchema>,
        response?: z.infer<ResponseSchema>
      ) => boolean;

      // be mindful of the size of the data you are sending here since it will be logged on every request
      paramsSelector?: (params: z.infer<ParamsSchema>) => NetworkAnalyticsDataRecord | undefined;
      responseSelector?: (
        response: z.infer<ResponseSchema>
      ) => NetworkAnalyticsDataRecord | undefined;
    };

export interface QueryRequesterParams<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  ResponseSchema extends z.ZodTypeAny,
  TransformedResultResult,
> {
  // defaults to "cap-srv"
  host: ApiHost;
  // defaults to "post"
  method: QueryMethod;

  path: Path;
  responseSchema: ResponseSchema;
  paramsSchema?: ParamsSchema;

  pathParams?: ExtractParamsFromPath<Path>;
  params?: Record<string, unknown>;

  transformer?: (response: z.infer<ResponseSchema>) => TransformedResultResult;
  // normalization/parsing to run after zod schema parsing

  signal?: AbortSignal;

  headers?: Record<string, string>;

  // defaults to "basic"
  analytics?: NetworkAnalyticsOptions<ParamsSchema, ResponseSchema>;

  // defaults to false
  assumeUnAuthenticated?: boolean;
}

function appendParamsToPath(path: string, params: Record<string, unknown>) {
  if (!params || Object.keys(params).length === 0) {
    return path;
  }

  const [basePath, existingQuery] = path.split("?");
  const searchParams = new URLSearchParams(existingQuery);

  // Merge in new params
  Object.entries(params).forEach(([key, value]) => {
    searchParams.append(key, value as string);
  });

  return `${basePath}?${searchParams.toString()}`;
}

function replacePathParams(path: string, params: Record<string, string> | undefined) {
  if (!params) {
    return path;
  }

  return path.replace(/:(\w+)/g, (_, p1) => params[p1]);
}

export async function queryRequester<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  ResponseSchema extends z.ZodTypeAny,
  Result = z.infer<ResponseSchema>,
>({
  path: originalPath,
  responseSchema,
  params = {},
  pathParams,
  transformer,
  method = "POST",
  signal,
  headers,
  analytics = { level: "basic" },
  host,
  assumeUnAuthenticated,
}: QueryRequesterParams<Path, ParamsSchema, ResponseSchema, Result>): Promise<Result> {
  let path: string = originalPath;

  const clientTrace = createClientTrace();

  const eventHost =
    typeof host === "string" ? host : host?.type === "custom" ? host.host : "cap-srv";

  // include the proxy to tell if it was proxied or not
  // but we do not need to include the params
  const eventProps = {
    host: eventHost,
    path,
    method,
    request_params: analytics.level === "custom" ? analytics.paramsSelector?.(params) : undefined,
    client_trace: clientTrace,
  };

  const start = performance.now();

  const shouldLogRequest =
    analytics.level === "custom"
      ? // eslint-disable-next-line prettier/prettier
        analytics.shouldLogRequest ?? (() => true)
      : () => analytics.level !== "none";

  const trackSuccess = (statusCode: number, responseData: ResponseType) => {
    if (shouldLogRequest("success", params, responseData)) {
      NetworkingEvents.request_stats.track({
        ...eventProps,
        status: "success",
        status_code: statusCode,
        latency: Math.round(performance.now() - start),

        response_data:
          analytics.level === "custom" ? analytics.responseSelector?.(responseData) : undefined,
      });
    }
  };

  const trackError = (
    statusCode: number,
    error_kind: "known" | "unknown",
    error_message?: string,
    error_code?: string
  ) => {
    if (shouldLogRequest("fail", params)) {
      NetworkingEvents.request_stats.track({
        ...eventProps,
        status: "fail",
        status_code: statusCode,
        latency: Math.round(performance.now() - start),

        error_kind,
        known_error_message: error_message,
        known_error_code: error_code,
      });
    }
  };

  const trackCancel = () => {
    if (shouldLogRequest("cancel", params)) {
      NetworkingEvents.request_stats.track({ ...eventProps, status: "cancel" });
    }
  };

  let res: ApiResponse | undefined;
  try {
    path = replacePathParams(path, pathParams);

    if (method === "DELETE" || method === "GET") {
      path = appendParamsToPath(path, params);
    }

    res = await makeApiRequest({
      host: host ?? "cap-srv",
      path,
      data: params,
      method,
      signal,
      clientTrace,
      headers,
      assumeUnAuthenticated,
    });

    const data = responseSchema.parse(res.data);

    // handle rare case in which schema validation succeeded but the status code is > 4xx
    if (res.ok) {
      trackSuccess(res.status, data);
    } else {
      trackError(res.status, data);
    }

    return transformer ? transformer(data) : data;
  } catch (e) {
    if (res) {
      const errorInfo = tryToGetErrorInfo(res.data);

      if (res.status === 503) {
        trackError(res.status, "unknown");
        throw new ApiUnavailableError(res.status, res.data);
      }

      if (errorInfo) {
        trackError(res.status, "known", errorInfo.message, errorInfo.code);
        throw new KnownApiError(errorInfo.message, res.status, errorInfo, res.data);
      }
    }

    if (isAbortOrCanceledError(e)) {
      trackCancel();
    } else {
      trackError(res?.status ?? -1, "unknown", `${e}`);
    }

    throw e;
  }
}

function tryToGetErrorInfo(data: any): { message: string; code?: string } | undefined {
  if (typeof data === "string") {
    return { message: data };
  }

  if (data instanceof Object && data !== null && "error" in data) {
    const error = data.error;

    if (error instanceof Object) {
      if ("message" in error && "code" in error) {
        const { message, code, ...rest } = error;
        return { message: message as string, code: code as string, ...rest };
      }
    }
  }

  if (data instanceof Object && data !== null && "message" in data) {
    const message = data.message as string;
    return { message };
  }

  if (data instanceof Object && data !== null && "reason" in data) {
    const reason = data.reason as string;
    return { message: reason };
  }
}
